import React, { useEffect, useState, useCallback, useRef } from 'react';
import { createAnomaly, fetchMyAnomalies, uploadAnomalyImage } from '../../lib/shiftApi';

const STATUS_LABEL = { pending: '待處理', acknowledged: '已受理', resolved: '已解決' };
const STATUS_CLS = { pending: 'st-pending', acknowledged: 'st-acknowledged', resolved: 'st-resolved' };
const MAX_IMAGES = 10;

function fmt(v) {
  try { return new Date(v).toLocaleString('zh-TW', { hour12: false }); }
  catch { return v; }
}

function AnomalyImages({ urls }) {
  const list = Array.isArray(urls) ? urls.filter(Boolean) : [];
  if (!list.length) return null;
  return (
    <div className="shift-anom-imgs">
      {list.map((url) => (
        <a key={url} href={url} target="_blank" rel="noopener noreferrer" className="shift-anom-img-link">
          <img src={url} alt="" className="shift-anom-img" />
        </a>
      ))}
    </div>
  );
}

/** 員工／同頁新增回報＋我的紀錄（留在 Home 導覽內）
 *  @param {{ formOnly?: boolean, defaultShowForm?: boolean, onSubmitted?: () => void }} props
 */
export default function ShiftAnomalyPanel({ formOnly = false, defaultShowForm = true, onSubmitted }) {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [imageUrls, setImageUrls] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [mine, setMine] = useState([]);
  const [loading, setLoading] = useState(!formOnly);
  const [toast, setToast] = useState('');
  const [showForm, setShowForm] = useState(defaultShowForm);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    if (formOnly) return;
    setLoading(true);
    try {
      const r = await fetchMyAnomalies();
      setMine(r?.reports || []);
    } catch { setMine([]); }
    finally { setLoading(false); }
  }, [formOnly]);

  useEffect(() => { load(); }, [load]);

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (imageUrls.length >= MAX_IMAGES) {
      setToast(`最多 ${MAX_IMAGES} 張圖`);
      setTimeout(() => setToast(''), 2000);
      return;
    }
    setUploading(true);
    try {
      const r = await uploadAnomalyImage(file);
      if (r?.ok && r.url) setImageUrls((prev) => [...prev, r.url].slice(0, MAX_IMAGES));
      else setToast('上傳失敗（限 JPG/PNG/WEBP，5MB）');
    } catch {
      setToast('上傳失敗（限 JPG/PNG/WEBP，5MB）');
    } finally {
      setUploading(false);
      setTimeout(() => setToast(''), 2000);
    }
  };

  const removeImage = (url) => {
    setImageUrls((prev) => prev.filter((u) => u !== url));
  };

  const submit = async () => {
    if (!title.trim()) { setToast('請填寫摘要'); return; }
    setSubmitting(true);
    try {
      const r = await createAnomaly({
        title: title.trim(),
        content,
        imageUrls,
      });
      if (r?.ok) {
        setTitle('');
        setContent('');
        setImageUrls([]);
        setToast('已送出回報');
        load();
        if (typeof onSubmitted === 'function') onSubmitted();
      } else setToast('送出失敗');
    } catch { setToast('送出失敗'); }
    finally {
      setSubmitting(false);
      setTimeout(() => setToast(''), 2000);
    }
  };

  const formBlock = (
    <div className="shift-anom-form">
      <div className="shift-login-field" style={{ marginBottom: 12 }}>
        <label>摘要 *</label>
        <input
          className="shift-login-inp"
          value={title}
          placeholder="例如：冰箱溫度異常"
          maxLength={200}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>
      <div className="shift-login-field" style={{ marginBottom: 12 }}>
        <label>詳細描述</label>
        <textarea
          className="shift-issue-ta"
          style={{ height: 110 }}
          value={content}
          placeholder="描述異常狀況、發生時間、影響範圍…"
          onChange={(e) => setContent(e.target.value)}
        />
      </div>
      <div className="shift-login-field" style={{ marginBottom: 12 }}>
        <label>附圖（選填，最多 {MAX_IMAGES} 張）</label>
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          style={{ display: 'none' }}
          onChange={onPickFile}
        />
        {imageUrls.length > 0 && (
          <div className="shift-anom-imgs" style={{ marginBottom: 8 }}>
            {imageUrls.map((url) => (
              <div key={url} className="shift-anom-img-wrap">
                <img src={url} alt="" className="shift-anom-img" />
                <button type="button" className="shift-anom-img-rm" onClick={() => removeImage(url)} aria-label="移除">×</button>
              </div>
            ))}
          </div>
        )}
        {imageUrls.length < MAX_IMAGES && (
          <button
            type="button"
            className="shift-mb-btn"
            style={{ border: '1px solid var(--border2)', fontWeight: 600 }}
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
          >
            {uploading ? '上傳中…' : '＋ 選擇照片'}
          </button>
        )}
      </div>
      {toast ? <div className="shift-login-err" style={{ marginBottom: 8 }}>{toast}</div> : null}
      <button type="button" className="shift-submit-btn" disabled={submitting || uploading} onClick={submit}>
        {submitting ? '送出中…' : '送出回報'}
      </button>
    </div>
  );

  if (formOnly) return formBlock;

  return (
    <div>
      <div style={{ padding: '8px 14px 0', display: 'flex', gap: 8 }}>
        <button
          type="button"
          className={`shift-mb-btn ${showForm ? 'on' : ''}`}
          style={{ flex: '0 0 auto', border: '1px solid var(--border2)', fontWeight: 700 }}
          onClick={() => setShowForm((v) => !v)}
        >
          {showForm ? '收合表單' : '＋ 新增回報'}
        </button>
      </div>

      {showForm ? formBlock : null}

      <div className="shift-slabel">我的回報紀錄</div>
      {loading && <div className="shift-empty">載入中…</div>}
      {!loading && mine.length === 0 && <div className="shift-empty">尚無回報紀錄</div>}
      {mine.map((r) => (
        <div key={r.id} className="shift-anom-item">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="shift-anom-title" style={{ flex: 1 }}>{r.title}</span>
            <span className={`shift-status-pill ${STATUS_CLS[r.status] || 'st-pending'}`}>
              {STATUS_LABEL[r.status] || r.status}
            </span>
          </div>
          {r.content ? <div className="shift-anom-body">{r.content}</div> : null}
          <AnomalyImages urls={r.imageUrls} />
          <div className="shift-anom-meta">
            <span>{String(r.bizDate).slice(0, 10)}</span>
            <span>{fmt(r.createdAt)}</span>
          </div>
          {r.resolvedNote ? (
            <div className="shift-anomaly-reply" style={{ marginTop: 6 }}>老闆回覆：{r.resolvedNote}</div>
          ) : null}
        </div>
      ))}
    </div>
  );
}
